import { Stack } from "@mui/material";
import { PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { DateRange, SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  Button,
  CalendarIcon,
  ChevronUpIcon,
  DatePickerVariant,
  Text,
  variantMap,
} from "@flat-ui";
import {
  CustomRangeCalendarLayout,
  PickersRangeLayoutProps,
} from "@components/date-range-picker/date-range-calendar-layout";
import {
  DateRangePickerProps,
  DateRangePreset,
} from "@components/date-range-picker/date-range-picker-types";
import {
  areRangesEqualByDay,
  buildDefaultPresets,
  formatRangeLabel,
  formatShortcutLabel,
  toDateRange,
  toDateTime,
  toDateTimeRange,
} from "@components/date-range-picker/date-range-picker-utils";

/** The width of the input field */
const INPUT_WIDTH = "12.5rem";

/** Class used to style the active shortcut chip in the calendar popover. */
const ACTIVE_SHORTCUT_CLASS = "DateRangePicker-shortcutActive";

/**
 * A customized MUI X DateRangePicker that follows the FARO design guidelines.
 * NOTE: relies on `@mui/x-date-pickers-pro`, which requires a commercial MUI X
 *
 * @returns a customized MUI DateRangePicker
 */
export function DateRangePicker({
  label,
  pickerTitle = "Select date range",
  value: sourceValue,
  defaultValue: sourceDefaultValue,
  minDate: sourceMinDate,
  maxDate: sourceMaxDate,
  presets,
  quickPresets,
  shouldEnableCompare = false,
  compareLabel = "Compare to previous timeframe",
  isCompareChecked,
  onCompareChange,
  variant = DatePickerVariant.Light,
  isReadOnly = false,
  isDisabled = false,
  onChange,
  onAccept,
}: DateRangePickerProps): JSX.Element {
  const [isOpen, updateIsOpen] = useState(false);

  const variantStyle = variantMap[variant];

  const isControlled = sourceValue !== undefined;
  const [internalValue, setInternalValue] = useState<DateRange<DateTime>>(() =>
    toDateTimeRange(sourceValue ?? sourceDefaultValue)
  );

  const pickerValue = internalValue;

  useEffect(() => {
    if (!isControlled) {
      return;
    }

    setInternalValue(toDateTimeRange(sourceValue));
  }, [isControlled, sourceValue]);

  const minDate = useMemo(() => toDateTime(sourceMinDate), [sourceMinDate]);
  const maxDate = useMemo(() => toDateTime(sourceMaxDate), [sourceMaxDate]);
  const selectedRangeLength = useMemo(
    () => formatRangeLabel(pickerValue),
    [pickerValue]
  );

  const applyRange = useCallback(
    (range: DateRange<DateTime>, shouldAccept = false) => {
      setInternalValue(range);
      onChange?.(toDateRange(range));
      if (shouldAccept) {
        onAccept?.(toDateRange(range));
      }
    },
    [onAccept, onChange]
  );

  const applyPreset = useCallback(
    (preset: DateRangePreset) => {
      const [start, end] = preset.getValue();
      applyRange([DateTime.fromJSDate(start), DateTime.fromJSDate(end)], true);
    },
    [applyRange]
  );

  const shortcutsItems: Array<PickersShortcutsItem<DateRange<DateTime>>> =
    useMemo(
      () =>
        (presets ?? buildDefaultPresets()).map(({ label, getValue }) => {
          const [start, end] = getValue();
          const presetRange: DateRange<DateTime> = [
            DateTime.fromJSDate(start),
            DateTime.fromJSDate(end),
          ];

          return {
            label: formatShortcutLabel(label),
            getValue: () => presetRange,
            className: areRangesEqualByDay(pickerValue, presetRange)
              ? ACTIVE_SHORTCUT_CLASS
              : undefined,
          };
        }),
      [pickerValue, presets]
    );

  const CustomLayoutWrapper = useCallback(
    (props: PickersRangeLayoutProps) => (
      <CustomRangeCalendarLayout
        variantStyle={variantStyle}
        pickerTitle={pickerTitle}
        shouldEnableCompare={shouldEnableCompare}
        compareLabel={compareLabel}
        isCompareChecked={isCompareChecked}
        onCompareChange={onCompareChange}
        {...props}
      />
    ),
    [
      isCompareChecked,
      compareLabel,
      shouldEnableCompare,
      onCompareChange,
      pickerTitle,
      variantStyle,
    ]
  );

  return (
    <>
      {label && <Text variant="heading12">{label}</Text>}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <DesktopDateRangePicker<DateTime>
          open={isOpen}
          disabled={isDisabled}
          readOnly={isReadOnly}
          onOpen={() => updateIsOpen(true)}
          onClose={() => updateIsOpen(false)}
          closeOnSelect={false}
          value={pickerValue}
          minDate={minDate ?? undefined}
          maxDate={maxDate ?? undefined}
          calendars={1}
          showDaysOutsideCurrentMonth
          reduceAnimations
          disableAutoMonthSwitching
          sx={{
            "& .MuiInputBase-root": {
              height: "2.375rem",
              width: INPUT_WIDTH,
              minWidth: INPUT_WIDTH,
              maxWidth: INPUT_WIDTH,
              backgroundColor: variantStyle.backgroundColor,
              color: variantStyle.inputFieldHoverColor,
              cursor: "pointer",
              "& .MuiInputBase-input": {
                cursor: "pointer",
              },
              "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                color: variantStyle.secondaryColor,
                fontSize: "1.125rem",
              },
            },
          }}
          slots={{
            field: SingleInputDateRangeField,
            layout: CustomLayoutWrapper,
          }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  // eslint-disable-next-line @typescript-eslint/naming-convention -- PopperJS API
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  options: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- PopperJS API
                    altAxis: false,
                    padding: 8,
                  },
                },
              ],
            },
            shortcuts: {
              items: shortcutsItems,
              changeImportance: "set",
            },
            actionBar: {
              actions: ["cancel", "accept"],
            },
            textField: {
              placeholder: "Jan 1 - Jan 31",
              inputProps: {
                value: selectedRangeLength,
              },
              onClick: () => {
                if (!isDisabled && !isReadOnly) {
                  updateIsOpen(true);
                }
              },
              InputProps: {
                // eslint-disable-next-line @typescript-eslint/naming-convention -- MUI API prop
                readOnly: true,
                sx: {
                  "& .MuiInputBase-input": {
                    color: variantStyle.inputFieldHoverColor,
                    caretColor: "transparent",
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    px: 0,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: variantStyle.secondaryColor,
                    opacity: 0.6,
                  },
                },
                startAdornment: (
                  <CalendarIcon sx={{ fontSize: "1.125rem", mr: 1 }} />
                ),
                endAdornment: (
                  <ChevronUpIcon
                    sx={{
                      fontSize: "1.125rem",
                      transition: "transform 0.2s",
                      transform: isOpen ? "none" : "rotate(180deg)",
                    }}
                  />
                ),
              },
            },
          }}
          onChange={(range) => applyRange(range)}
          onAccept={(range) => {
            updateIsOpen(false);
            // If only a start date was picked (end still null), treat it as a single-day range.
            const completeRange: DateRange<DateTime> = [
              range[0],
              range[1] ?? range[0],
            ];
            applyRange(completeRange, true);
          }}
        />
        {quickPresets?.map((preset) => (
          <Button
            key={preset.label}
            variant="secondary"
            disabled={isDisabled || isReadOnly}
            onClick={() => applyPreset(preset)}
            sx={{
              height: "2.375rem",
              whiteSpace: "nowrap",
              color: variantStyle.secondaryColor,
              borderColor: variantStyle.inputFieldDefaultBorderColor,
              "&:hover": {
                borderColor: variantStyle.selectedColor,
                color: variantStyle.selectedColor,
                backgroundColor: "transparent",
              },
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Stack>
    </>
  );
}
